import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import {
  ButtonRow,
  CancelButton,
  ConfirmButton,
  InputWrapper,
  Label,
  Input,
  ModalContent,
} from "./style";
import { ModalOverlay, ModalTitle } from "../../pages/Requests/style";
import Spinner from "../Spinner";
import { fetchDriverById, updateDriver } from "../../services/drivers";
import CustomDropdown from "../CustomDropdown";

const CarClass = {
  VIP: "VIP",
  NORMAL: "NORMAL",
  BUS_7_SEATS: "BUS_7_SEATS",
  BUS_15_SEATS: "BUS_15_SEATS",
  BUS_30_SEATS: "BUS_30_SEATS",
  BUS_50_SEATS: "BUS_50_SEATS",
};

const CarClassDisplay = {
  VIP: "VIP",
  NORMAL: "Normal",
  BUS_7_SEATS: "باص أبو 7 راكب",
  BUS_15_SEATS: "باص أبو 15 راكب",
  BUS_30_SEATS: "باص أبو 30 راكب",
  BUS_50_SEATS: "باص أبو 50 راكب",
};

const EditDriverModal = ({ driverId, onClose }) => {
  const queryClient = useQueryClient();

  const { data: driver, isLoading } = useQuery({
    queryKey: ["driver", driverId],
    queryFn: () => fetchDriverById(driverId),
    enabled: !!driverId,
  });

  const [formData, setFormData] = useState({
    name: "",
    phone_number: "",
    email: "",
    car_class: "",
    brand: "",
    license_plate: "",
  });

  const carClassOptions = useMemo(
    () =>
      Object.keys(CarClass).map((key) => ({
        value: CarClass[key],
        label: CarClassDisplay[key],
      })),
    []
  );

  useEffect(() => {
    if (!driver) return;

    const { user, vehicle } = driver;

    setFormData({
      name: user?.name ?? "",
      phone_number: user?.phone_number ?? "",
      email: user?.email ?? "",
      car_class: Object.values(CarClass).includes(vehicle?.car_class)
        ? vehicle.car_class
        : "",
      brand: vehicle?.brand ?? "",
      license_plate: vehicle?.license_plate ?? "",
    });
  }, [driver]);

  const mutation = useMutation({
    mutationFn: (data) =>
      updateDriver({
        id: driverId,
        data: {
          user: {
            name: data.name.trim(),
            phone_number: Number(data.phone_number),
            email: data.email.trim(),
          },
          vehicle: {
            car_class: data.car_class,
            brand: data.brand.trim(),
            license_plate: data.license_plate.trim(),
          },
        },
      }),

    onSuccess: () => {
      toast.success("✅ تم تحديث بيانات السائق");

      queryClient.invalidateQueries({
        queryKey: ["drivers", "accepted"],
      });

      queryClient.invalidateQueries({
        queryKey: ["driver", driverId],
      });

      onClose();
    },

    onError: (err) => {
      toast.error(err.response?.data?.message || "فشل تحديث البيانات");
    },
  });

  const handleChange = ({ target: { name, value } }) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = useCallback(() => {
    const phone = String(formData.phone_number).trim();

    const requiredFields = [
      "name",
      "phone_number",
      "email",
      "car_class",
      "brand",
      "license_plate",
    ];

    const hasEmpty = requiredFields.some(
      (key) => !String(formData[key]).trim()
    );

    if (hasEmpty) {
      toast.error("⚠️ جميع الحقول مطلوبة");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!emailRegex.test(formData.email.trim())) {
      toast.error("⚠️ البريد الإلكتروني غير صالح");
      return;
    }

    if (!/^\d+$/.test(phone)) {
      toast.error("⚠️ رقم الهاتف يجب أن يحتوي على أرقام فقط");
      return;
    }

    mutation.mutate({
      ...formData,
      phone_number: phone,
    });
  }, [formData, mutation]);

  if (isLoading) {
    return (
      <ModalOverlay>
        <ModalContent
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <Spinner />
        </ModalContent>
      </ModalOverlay>
    );
  }

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalTitle>تعديل بيانات السائق</ModalTitle>

        <InputWrapper>
          <Label>الاسم</Label>
          <Input
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="ادخل الاسم"
          />
        </InputWrapper>

        <InputWrapper>
          <Label>رقم الهاتف</Label>
          <Input
            name="phone_number"
            value={formData.phone_number}
            onChange={handleChange}
            placeholder="ادخل رقم الهاتف"
            dir="rtl"
          />
        </InputWrapper>

        <InputWrapper>
          <Label>البريد الإلكتروني</Label>
          <Input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="ادخل البريد الإلكتروني"
          />
        </InputWrapper>

        <InputWrapper>
          <Label>تصنيف المركبة</Label>

          <CustomDropdown
            options={carClassOptions}
            value={formData.car_class}
            onChange={(option) =>
              setFormData((prev) => ({
                ...prev,
                car_class: option.value,
              }))
            }
            placeholder="اختر تصنيف المركبة"
          />
        </InputWrapper>

        <InputWrapper>
          <Label>نوع السيارة</Label>
          <Input
            name="brand"
            value={formData.brand}
            onChange={handleChange}
            placeholder="ادخل نوع السيارة"
          />
        </InputWrapper>

        <InputWrapper>
          <Label>رقم اللوحة</Label>
          <Input
            name="license_plate"
            value={formData.license_plate}
            onChange={handleChange}
            placeholder="ادخل رقم اللوحة"
          />
        </InputWrapper>

        <ButtonRow>
          <CancelButton onClick={onClose}>
            إلغاء
          </CancelButton>

          <ConfirmButton
            onClick={handleSubmit}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? "جاري الحفظ..." : "حفظ"}
          </ConfirmButton>
        </ButtonRow>
      </ModalContent>
    </ModalOverlay>
  );
};

export default EditDriverModal;